// NOTE: All data fetching for SSR/SSG must be done in the server component/page/layout.
// This provider is synchronous and only initializes Zustand stores with the data passed as props.
// See: https://zustand.docs.pmnd.rs/guides/nextjs

import type { ListItem, MapItem, MapItemSUP, MapItemWithCoodId } from "@/types/search";
import type { ReactNode } from "react";
import { createContext, useContext, useRef } from "react";
import { useStore } from "zustand";
import { createInteractionStore, InteractionState } from "./stores/interaction-store";
import { createListDataStore, ListDataState, Pagination } from "./stores/list-store";
import { createLocationsStore, Location, LocationsState } from "./stores/locations-store";
import { Bounds, createMapDataStore, MapDataState } from "./stores/map-store";
export type ListDataStoreApi = ReturnType<typeof createListDataStore>;
export type MapDataStoreApi = ReturnType<typeof createMapDataStore>;
export type InteractionStoreApi = ReturnType<typeof createInteractionStore>;
export type LocationsStoreApi = ReturnType<typeof createLocationsStore>;
export interface SearchPageProviderProps {
  children: ReactNode;
  slug?: string; // URL slug for SSR-friendly filter initialization
  initialListData?: ListItem[];
  initialPagination?: Pagination;
  initialMapData?: MapItem[] | MapItemWithCoodId[];
  initialBounds?: Bounds | null;
  initialMapCenter?: {
    lat: number;
    lng: number;
  };
  initialMapZoom?: number | null;
  mapId?: string;
  initialFavorites?: MapItemSUP[];
  initialSelectedMarkers?: Record<string, MapItemSUP>;
  initialLocations?: Location[];
  initialTotalCount?: number | null;
}
export const ListDataStoreContext = createContext<ListDataStoreApi | undefined>(undefined);
export const MapDataStoreContext = createContext<MapDataStoreApi | undefined>(undefined);
export const InteractionStoreContext = createContext<InteractionStoreApi | undefined>(undefined);
export const LocationsStoreContext = createContext<LocationsStoreApi | undefined>(undefined);
export function SearchPageProvider({
  children,
  initialListData,
  initialPagination,
  initialMapData,
  initialBounds,
  initialMapCenter,
  initialMapZoom,
  mapId,
  initialFavorites,
  initialSelectedMarkers,
  initialLocations,
  initialTotalCount
}: SearchPageProviderProps) {
  const listDataStoreRef = useRef<ListDataStoreApi | null>(null);
  const mapDataStoreRef = useRef<MapDataStoreApi | null>(null);
  const interactionStoreRef = useRef<InteractionStoreApi | null>(null);
  const locationsStoreRef = useRef<LocationsStoreApi | null>(null);
  if (listDataStoreRef.current === null) {
    listDataStoreRef.current = createListDataStore({
      initialListData,
      initialPagination
    });
  }
  if (mapDataStoreRef.current === null) {
    // Convert MapItem[] to MapItemWithCoodId[] if needed
    const convertedMapData = initialMapData?.map((item: MapItem | MapItemWithCoodId) => {
      if (typeof item._id === "string") {
        return {
          ...item,
          _id: item._id.split(",").map(Number)
        } as MapItemWithCoodId;
      }
      return item as MapItemWithCoodId;
    });
    mapDataStoreRef.current = createMapDataStore({
      initialMapData: convertedMapData,
      initialBounds,
      initialMapCenter,
      initialMapZoom,
      mapId,
      initialTotalCount
    });
  }
  if (interactionStoreRef.current === null) {
    interactionStoreRef.current = createInteractionStore({
      initialFavorites,
      initialSelectedMarkers
    });
  }
  if (locationsStoreRef.current === null) {
    locationsStoreRef.current = createLocationsStore({
      initialLocations
    });
  }
  return <ListDataStoreContext.Provider value={listDataStoreRef.current}>
			<MapDataStoreContext.Provider value={mapDataStoreRef.current}>
				<InteractionStoreContext.Provider value={interactionStoreRef.current}>
					<LocationsStoreContext.Provider value={locationsStoreRef.current}>{children}</LocationsStoreContext.Provider>
				</InteractionStoreContext.Provider>
			</MapDataStoreContext.Provider>
		</ListDataStoreContext.Provider>;
}
export function useSearchPageListData(selector) {
  const store = useContext(ListDataStoreContext);
  if (!store) {
    throw new Error("useSearchPageListData must be used within SearchPageProvider");
  }
  return useStore(store, selector);
}
export function useSearchPageMapData(selector) {
  const store = useContext(MapDataStoreContext);
  if (!store) {
    throw new Error("useSearchPageMapData must be used within SearchPageProvider");
  }
  return useStore(store, selector);
}
export function useSearchPageInteraction(selector) {
  const store = useContext(InteractionStoreContext);
  if (!store) {
    throw new Error("useSearchPageInteraction must be used within SearchPageProvider");
  }
  return useStore(store, selector);
}
export function useSearchPageInteractionStore() {
  const store = useContext(InteractionStoreContext);
  if (!store) {
    throw new Error("useSearchPageInteractionStore must be used within SearchPageProvider");
  }
  return store;
}
export function useSearchPageLocations(selector) {
  const store = useContext(LocationsStoreContext);
  if (!store) {
    throw new Error("useSearchPageLocations must be used within SearchPageProvider");
  }
  return useStore(store, selector);
}
export function useSearchPageLocationsStore() {
  const store = useContext(LocationsStoreContext);
  if (!store) {
    throw new Error("useSearchPageLocationsStore must be used within SearchPageProvider");
  }
  return store;
}